<template>
  <div class="accordion">
    <div
      v-if="panels.length"
      class="accordion__wrapper"
    >
      <div
        v-for="(panel, index) in panels"
        :key="index"
        class="accordion__panel"
      >
        <button
          class="accordion__toggle"
          :class="{ [$class('active')]: isActive(index)}"
          :aria-expanded="isActive(index)"
          type="button"
          @click="togglePanel(index)"
        >
          <component
            :is="headingType"
            class="accordion__heading text-label-s"
            v-text="panel.title"
          />

          <animated-icon
            :delay-transition="false"
            :focusable="false"
            :transition-on-hover="false"
            transition-type="spin-180"
            :transition-by-prop="isActive(index)"
          >
            <template #iconOne>
              <icon-plus />
            </template>

            <template #iconTwo>
              <icon-minus />
            </template>
          </animated-icon>
        </button>

        <transition-height>
          <div
            v-show="isActive(index)"
            class="accordion__content"
          >
            <slot :name="`accordion-content-${index}`">
              <div v-html="panel.content" />
            </slot>
          </div>
        </transition-height>
      </div>
    </div>

    <div
      v-else-if="fallback?.title"
      class="accordion__fallback"
    >
      <button
        class="accordion__toggle"
        type="button"
      >
        <span
          class="accordion__heading text-label-s"
          v-text="fallback.title"
        />

        <animated-icon
          :delay-transition="false"
          :focusable="false"
          :transition-on-hover="true"
          transition-type="spin-180"
        >
          <template #iconOne>
            <icon-plus />
          </template>

          <template #iconTwo>
            <icon-minus />
          </template>
        </animated-icon>
      </button>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Accordion (accordion)
 * -----------------------------------------------------------------------------
 * Renders a set of accordion panels that can be opened and closed.
 *
 * @param {String} headingType - Element type for the toggle label.
 * @param {Array} panelData - Array of panel objects.
 * @param {Number/Boolean} activePanel - can pass in to the component which panel
 * is active on load
 *
 */
import TransitionHeight from '~global/core/transition-height'

import AnimatedIcon from '~global/animated-icon/animated-icon'

import IconPlus from '~icons/misc/plus.svg'
import IconMinus from '~icons/misc/minus.svg'

export default {
  name: 'Accordion',

  components: {
    TransitionHeight,
    AnimatedIcon,
    IconPlus,
    IconMinus,
  },

  props: {
    panelData: {
      type: Array,
      default: () => [],
    },
    headingType: {
      type: String,
      default: 'span',
    },
    activePanel: {
      type: [Number, Boolean],
      default: false,
    },
    expendMore: {
      type: Boolean,
      default: false,
    },
    fallback: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      panels: [],
    }
  },

  watch: {

    /**
     * Store panels on the component with a toggleable 'active' property.
     */
    panelData: {
      deep: true,
      immediate: true,
      handler() {
        this.formatPanels()
      },
    },
  },

  methods: {

    /**
     * Check if a panel is active using its index.
     * @param {Number} index - Index of a specific panel.
     * @returns {Boolean}
     */
    isActive(index) {
      return this.panels[index].active
    },

    /**
     * Toggle a panel's active state.
     * @param {Number} targetPanelIndex - Index of a specific panel.
     */
    togglePanel(targetPanelIndex) {
      if (this.isActive(targetPanelIndex)) {
        this.panels[targetPanelIndex].active = false
        return
      }

      if (this.expendMore) {
        this.panels[targetPanelIndex].active = true
      } else {
        this.panels.forEach((panel, index) => {
          panel.active = targetPanelIndex === index
        })
      }
    },

    /**
     * Store panels on the component with a toggleable 'active' property.
     */
    formatPanels() {
      this.panels = this.panelData.map((panel) => {
        return {
          active: false,
          ...panel,
        }
      })

      if (!this.activePanel && typeof this.activePanel === 'boolean') {
        return
      }
      this.panels[this.activePanel].active = true
    },
  },
}
</script>

<style lang="scss">
@import './accordion';
</style>
